/**
 * The Budgeting module's route table and the URLs its figures drill through to.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads them in — the paths, the permissions and the
 * components that implement them decided in one place by whoever owns the
 * module (the shape `modules/ar/routes.ts` established).
 *
 * The helpers below exist for the same reason `gl/drilldown.ts` exists: a
 * variance figure, an availability screen and a version's action bar all need to name
 * the same destination, and three inline template strings are three chances for
 * them to disagree. Every scoped link carries its filter, so the screen a user
 * lands on adds up to the figure they clicked.
 */

import type { RouteRecordRaw } from 'vue-router'

import { BUDGET_PERMISSIONS } from '@/modules/budget/permissions'
import { BUDGET_AVAILABILITY_ROUTES } from '@/modules/budget/availability/routes'
import { BUDGET_COMPARE_ROUTES } from '@/modules/budget/compare/routes'
import { BUDGET_CONTROL_LEVEL_ROUTES } from '@/modules/budget/control-levels/routes'
import { BUDGET_CYCLE_ROUTES, cycleRoute, cyclesRoute } from '@/modules/budget/cycles/routes'
import { BUDGET_FORECAST_ROUTES } from '@/modules/budget/forecast/routes'
import { BUDGET_SETTINGS_ROUTES } from '@/modules/budget/settings/routes'
import { BUDGET_SUPPLEMENTARY_ROUTES } from '@/modules/budget/supplementary/routes'
import { BUDGET_TRANSFER_ROUTES } from '@/modules/budget/transfers/routes'
import { BUDGET_VERSION_LIST_ROUTES } from '@/modules/budget/versions/routes'

export const BUDGET_ROUTES: RouteRecordRaw[] = [
  /* FT7-U22 (FSD 2.2.5 F06): a budget model is now a Budget Cycle. `/budget/models` and
   * `/budget/models/:id` are gone and redirect in `router/legacyRedirects.ts`; the ids carry
   * over because a cycle is the model row. */
  ...BUDGET_CYCLE_ROUTES,
  ...BUDGET_VERSION_LIST_ROUTES,
  ...BUDGET_COMPARE_ROUTES,
  ...BUDGET_AVAILABILITY_ROUTES,
  {
    path: '/budget/versions/:id(\\d+)',
    name: 'budget-version',
    component: () => import('@/modules/budget/VersionView.vue'),
    meta: {
      titleKey: 'budget.routeTitles.budgetVersion',
      permission: BUDGET_PERMISSIONS.versionRead,
    },
  },
  {
    path: '/budget/variance',
    name: 'budget-variance',
    component: () => import('@/modules/budget/VarianceView.vue'),
    meta: {
      titleKey: 'budget.routeTitles.budgetVariance',
      permission: BUDGET_PERMISSIONS.varianceRead,
    },
  },
  {
    path: '/budget/commitments',
    name: 'budget-commitments',
    component: () => import('@/modules/budget/CommitmentsView.vue'),
    meta: {
      titleKey: 'budget.routeTitles.budgetCommitments',
      permission: BUDGET_PERMISSIONS.commitmentRead,
    },
  },
  /* FT11-UC (FSD 2.2.5 F06 V06): the company-level budget policy screen is gone; the
   * per-cost-centre Control Levels screen replaces it. `/budget/policy` redirects in
   * `router/legacyRedirects.ts`. */
  ...BUDGET_CONTROL_LEVEL_ROUTES,
  ...BUDGET_FORECAST_ROUTES,
  ...BUDGET_SETTINGS_ROUTES,
  /* FT15-U23 (FSD 2.2.5 V05, V07): budget transfers and supplementary budget requests. */
  ...BUDGET_TRANSFER_ROUTES,
  ...BUDGET_SUPPLEMENTARY_ROUTES,
]

/* ------------------------------------------------------------------ links */

/** The cycles list — what `/budget/models` used to be. */
export function modelsRoute(): string {
  return cyclesRoute()
}

/** One budget cycle (the model row), reached from a version's header and links. */
export function modelRoute(id: number): string {
  return cycleRoute(id)
}

export function versionRoute(id: number): string {
  return `/budget/versions/${id}`
}

export interface VarianceScope {
  companyId: number
  /**
   * Omitted when the caller genuinely does not know it.
   *
   * A fiscal year is not a calendar year — `org`'s fiscal calendar decides where
   * it starts — so a caller holding only a document date must NOT guess one from
   * it. The report then opens scoped to the company with the year left for the
   * reader to choose, which is honest; prefilling `2026` because an invoice is
   * dated August would silently show the wrong year's variance to somebody who
   * had no reason to check.
   */
  fiscalYear?: number | null
  accountId?: number | null
  costCenterId?: number | null
  profitCenterId?: number | null
  branchId?: number | null
  fromPeriodNo?: number | null
  toPeriodNo?: number | null
}

export function varianceRoute(scope: VarianceScope): string {
  const params = new URLSearchParams()
  params.set('companyId', String(scope.companyId))
  appendId(params, 'fiscalYear', scope.fiscalYear)
  appendId(params, 'accountId', scope.accountId)
  appendId(params, 'costCenterId', scope.costCenterId)
  appendId(params, 'profitCenterId', scope.profitCenterId)
  appendId(params, 'branchId', scope.branchId)
  appendId(params, 'fromPeriodNo', scope.fromPeriodNo)
  appendId(params, 'toPeriodNo', scope.toPeriodNo)
  return `/budget/variance?${params.toString()}`
}

export interface CommitmentScope {
  companyId?: number | null
  accountId?: number | null
  fiscalPeriodId?: number | null
  status?: 'open' | 'released' | ''
}

/**
 * The commitment ledger, scoped to whatever the reader was looking at.
 *
 * A variance row's commitment figure is a sum of open commitment balances, and
 * the only useful answer to "why is that number what it is" is the list of the
 * documents behind it — which is what this link opens, filtered to exactly the
 * account and period the figure was computed over.
 */
export function commitmentsRoute(scope: CommitmentScope = {}): string {
  const params = new URLSearchParams()
  appendId(params, 'companyId', scope.companyId)
  appendId(params, 'accountId', scope.accountId)
  appendId(params, 'fiscalPeriodId', scope.fiscalPeriodId)
  if (scope.status !== undefined && scope.status !== '') params.set('status', scope.status)
  const query = params.toString()
  return query === '' ? '/budget/commitments' : `/budget/commitments?${query}`
}

function appendId(params: URLSearchParams, key: string, value: number | null | undefined): void {
  if (value === undefined || value === null) return
  params.set(key, String(value))
}
